import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, qs, useAction, useApi } from '../lib/api';
import { acct, date, dateTime, money, parseAmount, period, titleCase, todayIso } from '../lib/format';
import { AccountSelect, useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { AuditRow, Entry, EntryWithLines, Line, Workflow } from '../lib/types';
import { EntryLines, useActorLabel } from '../components/entry';
import { Icons } from '../components/icons';
import { Badge, Button, ButtonLink, Card, cx, Empty, ErrorNote, Field, Input, KeyValues, Modal, MoneyInput, PageHeader, Query, Select, SourceBadge, StatusBadge, Table, Td, Th, Tr } from '../components/ui';

type EntryRow = Entry & { lines: Line[] };

const SOURCE_TYPES = ['MANUAL', 'INVOICE', 'PAYMENT', 'BILL', 'BILL_PAYMENT', 'REVENUE_SCHEDULE', 'AMORTIZATION', 'ACCRUAL', 'DEPRECIATION', 'BANK', 'REVERSAL'];

export function JournalPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const [f, setF] = useState({ status: '', sourceType: '', submissionSource: '', search: '', fromDate: '', toDate: '' });
  const [pages, setPages] = useState<string[]>(['']);
  const set = (k: keyof typeof f, v: string) => {
    setF((x) => ({ ...x, [k]: v }));
    setPages(['']);
  };

  return (
    <>
      <PageHeader
        title="Journal entries"
        description="Every posting in the general ledger. Posted entries are immutable and sealed into the hash chain; corrections are reversals."
        actions={
          can('ACCOUNTANT') && (
            <ButtonLink to="/journal/new" variant="primary" icon={<Icons.Plus size={15} />}>
              New entry
            </ButtonLink>
          )
        }
      />
      <Card pad={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <div className="relative w-64">
            <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint">
              <Icons.Search size={15} />
            </span>
            <Input className="pl-8" placeholder="Memo or entry number" value={f.search} onChange={(e) => set('search', e.target.value)} />
          </div>
          <Select className="w-40" value={f.status} onChange={(e) => set('status', e.target.value)}>
            <option value="">Any status</option>
            {['POSTED', 'PENDING_APPROVAL', 'DRAFT', 'REVERSED', 'REJECTED'].map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </Select>
          <Select className="w-44" value={f.sourceType} onChange={(e) => set('sourceType', e.target.value)}>
            <option value="">Any origin</option>
            {SOURCE_TYPES.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </Select>
          <Select className="w-36" value={f.submissionSource} onChange={(e) => set('submissionSource', e.target.value)}>
            <option value="">Any channel</option>
            {['UI', 'API', 'MCP', 'AI', 'IMPORT', 'SYSTEM'].map((s) => (
              <option key={s} value={s}>
                {s === 'UI' ? 'Manual' : s === 'SYSTEM' ? 'System' : s}
              </option>
            ))}
          </Select>
          <Input type="date" className="w-36" value={f.fromDate} onChange={(e) => set('fromDate', e.target.value)} aria-label="From date" />
          <span className="text-muted">–</span>
          <Input type="date" className="w-36" value={f.toDate} onChange={(e) => set('toDate', e.target.value)} aria-label="To date" />
        </div>
        <Table>
          <thead>
            <tr>
              <Th w="72px">No.</Th>
              <Th w="110px">Date</Th>
              <Th>Memo</Th>
              <Th>Origin</Th>
              <Th>Channel</Th>
              <Th right>Amount</Th>
              <Th>Status</Th>
              <Th w="36px" />
            </tr>
          </thead>
          <tbody>
            {pages.map((before, i) => (
              <JournalPageRows key={before || 'first'} filters={f} before={before} onRow={(id) => navigate(`/journal/${id}`)} onMore={i === pages.length - 1 ? (id) => setPages((p) => [...p, id]) : undefined} />
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function JournalPageRows({ filters, before, onRow, onMore }: { filters: Record<string, string>; before: string; onRow: (id: string) => void; onMore?: (lastId: string) => void }) {
  const { currency, can } = useSession();
  const subs = useSubsidiaries();
  const q = useApi<EntryRow[]>(`/api/journal-entries${qs({ ...filters, before, limit: 50 })}`);
  if (q.isLoading)
    return (
      <tr>
        <Td colSpan={8} muted className="py-8 text-center">
          Loading…
        </Td>
      </tr>
    );
  if (q.error)
    return (
      <tr>
        <Td colSpan={8}>
          <ErrorNote error={q.error} />
        </Td>
      </tr>
    );
  const rows = q.data ?? [];
  if (!rows.length && !before)
    return (
      <tr>
        <Td colSpan={8}>
          {Object.values(filters).some(Boolean) ? (
            <Empty title="No entries match these filters" />
          ) : (
            <Empty
              title="No journal entries yet"
              action={
                can('ACCOUNTANT') && (
                  <ButtonLink to="/journal/new" variant="primary" icon={<Icons.Plus size={15} />}>
                    New entry
                  </ButtonLink>
                )
              }
            >
              Invoices, bills and payments post here on their own. Record an opening balance or a one-off adjustment by hand.
            </Empty>
          )}
        </Td>
      </tr>
    );
  return (
    <>
      {rows.map((e) => {
        const subCur = subs.byId.get(e.subsidiaryId)?.currency ?? currency;
        return (
          <Tr key={e.id} onClick={() => onRow(e.id)}>
            <Td className="num text-muted">{e.number}</Td>
            <Td className="num whitespace-nowrap">{date(e.date)}</Td>
            <Td className="max-w-[420px]">
              <div className="truncate">{e.memo || <span className="text-faint">No memo</span>}</div>
              <div className="truncate text-[11.5px] text-muted">
                {e.lines.length} lines · {subs.byId.get(e.subsidiaryId)?.name}
                {e.currency !== subCur && ` · ${e.currency}`}
              </div>
            </Td>
            <Td muted className="text-[12.5px] whitespace-nowrap">
              {titleCase(e.sourceType)}
            </Td>
            <Td>
              <SourceBadge source={e.submissionSource} />
            </Td>
            <Td right>{money(e.totalBase, subCur)}</Td>
            <Td>
              <StatusBadge status={e.status} label={e.status === 'PENDING_APPROVAL' ? 'Needs approval' : undefined} />
            </Td>
            <Td className="text-faint">{e.hash && <Icons.Chain size={14} aria-label="Sealed" />}</Td>
          </Tr>
        );
      })}
      {onMore && rows.length === 50 && (
        <tr>
          <Td colSpan={8} className="border-b-0 text-center">
            <Button variant="ghost" size="sm" onClick={() => onMore(rows[rows.length - 1]!.id)}>
              Load older entries
            </Button>
          </Td>
        </tr>
      )}
    </>
  );
}

const SOURCE_LINK: Record<string, (id: string) => string> = {
  INVOICE: (id) => `/ar/invoices/${id}`,
  BILL: (id) => `/ap/bills/${id}`,
};

export function JournalEntryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useSession();
  const subs = useSubsidiaries();
  const actor = useActorLabel();
  const q = useApi<EntryWithLines>(`/api/journal-entries/${id}`);
  const audit = useApi<AuditRow[]>(`/api/audit-log${qs({ entityType: 'journal_entry', entityId: id })}`);
  const [reversing, setReversing] = useState(false);
  const [revDate, setRevDate] = useState(todayIso());

  const post = useAction(() => api(`/api/journal-entries/${id}/post`, { method: 'POST' }), { success: 'Entry posted' });
  const del = useAction(() => api(`/api/journal-entries/${id}`, { method: 'DELETE' }), { success: 'Draft deleted' });
  const reverse = useAction((d: string) => api<EntryWithLines>(`/api/journal-entries/${id}/reverse`, { body: { date: d } }), { success: 'Reversal posted' });

  return (
    <Query q={q}>
      {({ entry: e, lines }) => {
        const sub = subs.byId.get(e.subsidiaryId);
        const fx = sub && e.currency !== sub.currency;
        const link = e.sourceId && SOURCE_LINK[e.sourceType]?.(e.sourceId);
        return (
          <>
            <PageHeader
              eyebrow={
                <Link to="/journal" className="hover:text-ink">
                  ← Journal entries
                </Link>
              }
              title={
                <span className="flex items-center gap-3">
                  Entry #{e.number} <StatusBadge status={e.status} label={e.status === 'PENDING_APPROVAL' ? 'Needs approval' : undefined} />
                </span>
              }
              description={e.memo}
              actions={
                <>
                  {e.status === 'DRAFT' && can('ACCOUNTANT') && (
                    <>
                      <Button variant="danger" loading={del.isPending} onClick={() => del.mutate(undefined, { onSuccess: () => navigate('/journal') })}>
                        Delete draft
                      </Button>
                      <Button variant="primary" loading={post.isPending} onClick={() => post.mutate()}>
                        Post entry
                      </Button>
                    </>
                  )}
                  {e.status === 'PENDING_APPROVAL' && <ButtonLink to="/approvals">Go to approvals</ButtonLink>}
                  {e.status === 'POSTED' && !e.reversedBy && can('ACCOUNTANT') && (
                    <Button icon={<Icons.Undo size={15} />} onClick={() => setReversing(true)}>
                      Reverse
                    </Button>
                  )}
                </>
              }
            />
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-5">
                <Card title="Lines" subtitle={fx ? `Entered in ${e.currency} at ${Number(e.fxRate)} → ${sub?.currency}` : undefined} pad={false}>
                  <EntryLines lines={lines} currency={e.currency} />
                </Card>
                {fx && (
                  <Card title={`Functional amounts (${sub?.currency})`} pad={false}>
                    <EntryLines lines={lines} currency={sub!.currency} base compact />
                  </Card>
                )}
                <Card title="Activity" pad={false}>
                  <Query q={audit}>
                    {(rows) =>
                      rows.length ? (
                        <ul className="divide-y divide-line">
                          {rows.map((r) => (
                            <li key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-[12.5px]">
                              <span className="size-1.5 rounded-full bg-line-strong" />
                              <span className="font-medium">{titleCase(r.action.split('.').pop())}</span>
                              <span className="text-muted">by {actor(r.actorId.replace(/^user:/, ''))}</span>
                              <SourceBadge source={r.source} />
                              <span className="ml-auto text-muted">{dateTime(r.createdAt)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Empty title="No recorded activity" />
                      )
                    }
                  </Query>
                </Card>
              </div>
              <div className="space-y-5">
                <Card title="Details">
                  <KeyValues
                    cols={1}
                    items={[
                      ['Date', `${date(e.date)} · ${period(e.period)}`],
                      ['Entity', sub?.name],
                      ['Origin', link ? <Link to={link} className="text-brand hover:underline">{titleCase(e.sourceType)} →</Link> : titleCase(e.sourceType)],
                      ['Channel', <SourceBadge key="s" source={e.submissionSource} />],
                      ['Created by', actor(e.createdBy)],
                      ['Approved by', e.approvedBy ? actor(e.approvedBy) : '—'],
                      ['Posted', e.postedAt ? dateTime(e.postedAt) : '—'],
                      ['Auto-reverses', e.autoReverseDate ? date(e.autoReverseDate) : '—'],
                      ...(e.reversalOf ? ([['Reverses', <Link key="r" to={`/journal/${e.reversalOf}`} className="text-brand hover:underline">Original entry →</Link>]] as [string, React.ReactNode][]) : []),
                      ...(e.reversedBy ? ([['Reversed by', <Link key="r" to={`/journal/${e.reversedBy}`} className="text-brand hover:underline">Reversal entry →</Link>]] as [string, React.ReactNode][]) : []),
                    ]}
                  />
                </Card>
                <Card title={<span className="flex items-center gap-2"><Icons.Chain size={15} /> Hash chain</span>}>
                  {e.hash ? (
                    <div className="space-y-2.5 text-[12px]">
                      <div>
                        <div className="text-muted">Sequence</div>
                        <div className="num font-medium">#{e.chainSeq}</div>
                      </div>
                      <div>
                        <div className="text-muted">Entry hash (SHA-256)</div>
                        <div className="font-mono text-[11px] break-all text-ink-2">{e.hash}</div>
                      </div>
                      <div>
                        <div className="text-muted">Previous hash</div>
                        <div className="font-mono text-[11px] break-all text-muted">{e.prevHash}</div>
                      </div>
                      <p className="text-muted">Changing any posted amount, account or date would break every hash after this one. Controls re-verify the chain continuously.</p>
                    </div>
                  ) : (
                    <p className="text-[12.5px] text-muted">Sealed into the chain when the entry posts.</p>
                  )}
                </Card>
              </div>
            </div>
            <Modal
              open={reversing}
              onClose={() => setReversing(false)}
              title={`Reverse entry #${e.number}`}
              footer={
                <>
                  <Button onClick={() => setReversing(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    loading={reverse.isPending}
                    onClick={() =>
                      reverse.mutate(revDate, {
                        onSuccess: (r) => {
                          setReversing(false);
                          navigate(`/journal/${r.entry.id}`);
                        },
                      })
                    }
                  >
                    Post reversal
                  </Button>
                </>
              }
            >
              <p className="mb-3 text-[13px] text-muted">Posts a mirror entry that swaps every debit and credit. The original stays in the ledger, marked reversed.</p>
              <Field label="Reversal date" hint="Must fall in an open period.">
                <Input type="date" value={revDate} onChange={(x) => setRevDate(x.target.value)} />
              </Field>
            </Modal>
          </>
        );
      }}
    </Query>
  );
}

interface DraftLine {
  key: number;
  accountId: string;
  description: string;
  department: string;
  debit: string;
  credit: string;
}

let lineKey = 0;
const blankLine = (): DraftLine => ({ key: ++lineKey, accountId: '', description: '', department: '', debit: '', credit: '' });

export function NewJournalEntryPage() {
  const navigate = useNavigate();
  const subs = useSubsidiaries();
  const [subsidiaryId, setSub] = useState('');
  const [entryDate, setDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [currency, setCurrency] = useState('');
  const [autoReverse, setAutoReverse] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine(), blankLine()]);
  const sub = subs.list.find((s) => s.id === subsidiaryId) ?? subs.list[0];
  const cur = currency || sub?.currency || 'USD';
  const base = useSession().currency;
  const workflows = useApi<Workflow[]>('/api/approval-workflows');

  const parsed = lines.map((l) => ({ ...l, d: parseAmount(l.debit, cur), c: parseAmount(l.credit, cur) }));
  const totalD = parsed.reduce((s, l) => s + (l.d ?? 0), 0);
  const totalC = parsed.reduce((s, l) => s + (l.c ?? 0), 0);
  const used = parsed.filter((l) => l.accountId || l.d || l.c);
  // The same test as core findWorkflow, for a hand-entered entry. Other currencies convert on the server, so only base-currency entries get the hint.
  const accountIds = used.map((l) => l.accountId).filter(Boolean);
  const workflow =
    cur === base && totalD > 0 && totalD === totalC
      ? (workflows.data ?? []).find((w) => {
          const c = w.conditions;
          if (!w.active || w.entityType !== 'JOURNAL_ENTRY' || c.vendorIds?.length) return false;
          if (c.minAmount !== undefined && totalD < c.minAmount) return false;
          if (c.submissionSources?.length && !c.submissionSources.includes('UI')) return false;
          return !c.accountIds?.length || accountIds.some((a) => c.accountIds!.includes(a));
        })
      : undefined;
  const problems = useMemo(() => {
    const p: string[] = [];
    if (used.length < 2) p.push('Add at least two lines.');
    if (used.some((l) => !l.accountId)) p.push('Every line needs an account.');
    if (used.some((l) => (l.debit && l.d === null) || (l.credit && l.c === null))) p.push('Some amounts are not valid numbers.');
    if (used.some((l) => !!l.d === !!l.c)) p.push('Each line needs either a debit or a credit.');
    if (totalD !== totalC) p.push(`Debits and credits differ by ${money(Math.abs(totalD - totalC), cur)}.`);
    return p;
  }, [used, totalD, totalC, cur]);

  const create = useAction(
    (mode: 'POST' | 'DRAFT') =>
      api<EntryWithLines>('/api/journal-entries', {
        body: {
          subsidiaryId: sub?.id,
          date: entryDate,
          memo: memo || undefined,
          currency: cur,
          autoReverseDate: autoReverse || undefined,
          mode,
          lines: used.map((l) => ({ accountId: l.accountId, description: l.description || undefined, department: l.department || undefined, debit: l.d || undefined, credit: l.c || undefined })),
        },
      }),
    { success: (r) => (r.entry.status === 'PENDING_APPROVAL' ? 'Sent for approval (workflow matched)' : r.entry.status === 'DRAFT' ? 'Draft saved' : `Entry #${r.entry.number} posted`) },
  );

  const update = (key: number, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/journal" className="hover:text-ink">
            ← Journal entries
          </Link>
        }
        title="New journal entry"
        description="Manual entries over the approval threshold go to the approvals inbox instead of posting straight away."
      />
      <Card>
        <div className="grid gap-4 md:grid-cols-5">
          <Field label="Entity">
            <Select value={sub?.id ?? ''} onChange={(e) => setSub(e.target.value)}>
              {subs.list.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date">
            <Input type="date" value={entryDate} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Currency" hint={sub && cur !== sub.currency ? `Converted to ${sub.currency} at the latest rate` : undefined}>
            <Select value={cur} onChange={(e) => setCurrency(e.target.value)}>
              {['USD', 'GBP', 'EUR', 'INR', 'CAD', 'AUD'].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>
          <Field label="Auto-reverse on" hint="Optional, for accruals">
            <Input type="date" value={autoReverse} onChange={(e) => setAutoReverse(e.target.value)} />
          </Field>
          <Field label="Memo" className="md:col-span-5">
            <Input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="What is this entry for?" />
          </Field>
        </div>
      </Card>
      <Card className="mt-4" pad={false}>
        <Table>
          <thead>
            <tr>
              <Th w="30%">Account</Th>
              <Th>Description</Th>
              <Th w="140px">Department</Th>
              <Th right w="150px">
                Debit
              </Th>
              <Th right w="150px">
                Credit
              </Th>
              <Th w="40px" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <Td>
                  <AccountSelect value={l.accountId} onChange={(v) => update(l.key, { accountId: v })} />
                </Td>
                <Td>
                  <Input value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} />
                </Td>
                <Td>
                  <Input value={l.department} onChange={(e) => update(l.key, { department: e.target.value })} placeholder="Optional" />
                </Td>
                <Td>
                  <MoneyInput value={l.debit} onChange={(e) => update(l.key, { debit: e.target.value, credit: e.target.value ? '' : l.credit })} placeholder="0.00" />
                </Td>
                <Td>
                  <MoneyInput value={l.credit} onChange={(e) => update(l.key, { credit: e.target.value, debit: e.target.value ? '' : l.debit })} placeholder="0.00" />
                </Td>
                <Td>
                  <button onClick={() => setLines((ls) => (ls.length > 2 ? ls.filter((x) => x.key !== l.key) : ls))} className="rounded p-1 text-faint hover:bg-sunken hover:text-bad" aria-label="Remove line">
                    <Icons.Trash size={15} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td colSpan={3} className="border-b-0">
                <Button size="sm" variant="ghost" icon={<Icons.Plus size={14} />} onClick={() => setLines((ls) => [...ls, blankLine()])}>
                  Add line
                </Button>
              </Td>
              <Td right className="border-b-0 font-semibold">
                {acct(totalD, cur)}
              </Td>
              <Td right className="border-b-0 font-semibold">
                {acct(totalC, cur)}
              </Td>
              <Td className="border-b-0" />
            </tr>
          </tfoot>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
          <div className="text-[12.5px]">
            {problems.length ? (
              <span className="text-warn">{problems[0]}</span>
            ) : (
              <span className="flex items-center gap-1.5 text-good">
                <Icons.Check size={15} /> Balanced
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button disabled={problems.length > 0} loading={create.isPending && create.variables === 'DRAFT'} onClick={() => create.mutate('DRAFT', { onSuccess: (r) => navigate(`/journal/${r.entry.id}`) })}>
              Save draft
            </Button>
            <Button variant="primary" disabled={problems.length > 0} loading={create.isPending && create.variables === 'POST'} onClick={() => create.mutate('POST', { onSuccess: (r) => navigate(`/journal/${r.entry.id}`) })}>
              Post entry
            </Button>
          </div>
        </div>
        {create.error && <ErrorNote error={create.error} className="mx-4 mb-4" />}
      </Card>
      {workflow && (
        <div className={cx('mt-3 flex items-center gap-2 text-[12.5px] text-muted')}>
          <Badge tone="warn">Approval</Badge> Posting sends this entry for approval first: it matches the “{workflow.name}” workflow.
        </div>
      )}
    </>
  );
}
